/**
 * Thinking Bubble — living neural organism.
 *
 * Adapted from the aria project. A field of coupled Kuramoto oscillators
 * (neurons) drifts within a harmonic field. Neurons form fading
 * curved connections when close; Hebbian learning tunes coupling; STDP
 * tunes weights. Neurogenesis grows the population toward a resting size.
 * The result: a small self-organising organism that feels alive.
 *
 * Drop it anywhere you want a "something is happening" signal.
 */
import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins'
import { css, html, type PropertyValues } from 'lit'
import { property } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { styleMap } from 'lit/directives/style-map.js'
import { fromResizeObserver } from '@schmancy/directives/layout'
import { EMPTY, from, fromEvent, merge, Observable, Subject } from 'rxjs'
import { distinctUntilChanged, map, skip, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators'

function intersection$(target: Element): Observable<boolean> {
	return new Observable<boolean>(subscriber => {
		const obs = new IntersectionObserver(entries => subscriber.next(entries[0]!.isIntersecting), {
			threshold: 0,
		})
		obs.observe(target)
		return () => obs.disconnect()
	})
}

interface Neuron {
	x: number
	y: number
	baseX: number
	baseY: number
	phaseX: number
	phaseY: number
	freqX: number
	freqY: number
	ampX: number
	ampY: number
	radius: number
	opacity: number
	glowRadius: number
	driftVx: number
	driftVy: number
	phase: number
	naturalFreq: number
	freqTarget: number
	id: number
	birthTime: number
	maturity: number
	targetRadius: number
	targetOpacity: number
	targetGlowRadius: number
	trace: number
}

interface BlobHarmonic {
	n: number
	amp: number
	speed: number
	phase: number
}

interface RGB {
	r: number
	g: number
	b: number
}
interface Palette {
	red: RGB
	blue: RGB
	pink: RGB
	cyan: RGB
	magenta: RGB
}

interface FadingConnection {
	i: number
	j: number
	alpha: number
	cpx: number
	cpy: number
	cpvx: number
	cpvy: number
	weight: number
	coupling: number
}

const MAX_N = 24
const SEED_N = 10
const W = 240
const H = 240
const CX = W / 2
const CY = H / 2
const BASE_R = 76
const CONNECT_DIST = 110
const COUPLING = 0.0008
const FADE_RATE = 0.003
// Below this rendered width the 240px drawing, shrunk by the browser, thins to
// sub-pixel strokes and dots and reads as a faint speck.
const COMPACT_BELOW = 96

const HARMONICS: BlobHarmonic[] = [
	{ n: 2, amp: 14, speed: 0.00035, phase: 0 },
	{ n: 3, amp: 10, speed: 0.00055, phase: 1.2 },
	{ n: 4, amp: 6, speed: 0.00028, phase: 2.5 },
	{ n: 5, amp: 4, speed: 0.00072, phase: 0.8 },
	{ n: 7, amp: 2.4, speed: 0.0005, phase: 3.7 },
]

const TAU_MATURE = 2000
const STDP_A_PLUS = 0.005
const STDP_A_MINUS = 0.00525
const STDP_TAU = 500
const HEBBIAN_EPSILON = 0.00003
const HEBBIAN_ALPHA = 1.0

function reducedMotion(): boolean {
	return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

@schmancyElement('schmancy-thinking-bubble')
export class SchmancyThinkingBubble extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				width: 240px;
				height: 240px;
			}
			:host([animated]):not([word-motion="breath"]):not([word-motion="current"]):not([word-motion="orbit"]) {
				animation:
					bubble-enter 0.6s ease-out forwards,
					bubble-breathe 6s ease-in-out 0.6s infinite,
					bubble-drift 12s ease-in-out infinite;
				will-change: opacity, transform;
			}

			@keyframes bubble-enter {
				from {
					opacity: 0;
					transform: scale(0.3);
				}
				to {
					opacity: 1;
					transform: scale(1);
				}
			}

			@keyframes bubble-breathe {
				0%,
				100% {
					transform: scale(1);
				}
				50% {
					transform: scale(1.06);
				}
			}

			@keyframes bubble-drift {
				0%,
				100% {
					translate: 0 0;
				}
				25% {
					translate: 6px -4px;
				}
				50% {
					translate: -3px 5px;
				}
				75% {
					translate: -5px -2px;
				}
			}

			@media (prefers-reduced-motion: reduce) {
				:host {
					animation: none !important;
				}
			}
		`,
	]

	/** Freeze the native neural image without recurring animation work. */
	@property({ type: Boolean, reflect: true }) animated = true
	@property({ type: Number }) seeds = SEED_N
	/** Colour temperature shift (-1 = cool/blue, 0 = neutral, 1 = warm/pink). Default: 0. */
	@property({ type: Number }) temperature = 0
	/** A word-shaped opening that dissolves into the neural field. Changing it replays the opening. */
	@property({ type: String, attribute: 'start-word' }) startWord = ''
	/** Organic entrance and continuous paths into the same neural field. Empty retains the classic opening. */
	@property({ type: String, attribute: 'word-motion', reflect: true }) wordMotion: '' | 'breath' | 'current' | 'orbit' = ''
	/** Font-family for the complete word, including mixed scripts. Defaults to the host's inherited family. */
	@property({ type: String, attribute: 'word-font' }) wordFont = ''
	/** Reform the opening source after the neural phase and repeat without resampling it. */
	@property({ type: Boolean }) loop = false

	/** Prepared transparent image, fitted without distortion. A new canvas identity restarts the opening. */
	@property({ attribute: false }) startShape: HTMLCanvasElement | null = null

	/** Self-contained SVG markup, decoded as an inert image. Takes precedence over canvas and text. */
	@property({ type: String, attribute: 'start-svg' }) startSvg = ''
	/** One CSS color for every phase. Empty preserves source colors and the neural palette. */
	@property({ type: String }) color = ''

	private svgShape: HTMLImageElement | null = null
	private svgChanges = new Subject<void>()
	private inkColor: RGB | null = null
	private sourcePalette: RGB[] = []
	private inkAlpha = 1
	private shapeMask: HTMLCanvasElement | null = null
	private sourceFrame: HTMLCanvasElement | null = null
	private wordPoints: { x: number; y: number; phase: number; target: number; coverage: number; color?: string }[] = []
	private wordElapsed = 0
	private shapePointRadius = 0.7

	private wrapperRef = createRef<HTMLDivElement>()
	private canvas: HTMLCanvasElement | null = null
	private ctx: CanvasRenderingContext2D | null = null
	private rafId = 0
	private lastDrawTime = 0
	private hasDrawn = false
	private animationChanges = new Subject<void>()

	private neurons: Neuron[] = []
	private nextNeuronId = 0
	private connections = new Map<number, FadingConnection>()
	private activeKeys = new Set<number>()

	private sinPhase = new Float64Array(MAX_N)
	private prevSinPhase = new Float64Array(MAX_N)

	private isVisible = false
	private tabVisible = true
	/** One CSS pixel in drawing units while compact, 0 at full size. */
	private compactUnit = 0

	override connectedCallback(): void {
		super.connectedCallback()
		from(Promise.all([this.updateComplete, document.fonts?.ready]))
			.pipe(
				take(1),
				switchMap(() => {
					const wrapper = this.wrapperRef.value
					if (!wrapper) return EMPTY

					this.canvas = document.createElement('canvas')
					this.canvas.className = this.startShape || this.startSvg.trim() ? 'block w-full h-full' : 'block w-full h-full motion-reduce:hidden'
					this.canvas.setAttribute('aria-hidden', 'true')
					wrapper.prepend(this.canvas)

					this.ctx = this.canvas.getContext('2d')
					if (!this.ctx) return EMPTY

					this.sizeCanvas()
					this.resolveColor()
					this.initNeurons()

					this.tabVisible = document.visibilityState === 'visible'
					return merge(
						this.svgChanges.pipe(
							startWith(undefined),
							switchMap(() => new Observable<void>(subscriber => {
								if (!this.startSvg.trim()) {
									this.svgShape = null
									this.initNeurons()
									this.hasDrawn = false
									subscriber.next(); subscriber.complete(); return
								}
								const parsed = new DOMParser().parseFromString(this.startSvg, 'image/svg+xml')
								const svg = parsed.documentElement
								if (svg.localName !== 'svg' || parsed.querySelector('parsererror')) {
									this.svgShape = null
									this.initNeurons()
									this.hasDrawn = false
									this.dispatchEvent(new CustomEvent('shape-error', { detail: { message: componentText('thinking-bubble.invalidSvg') }, bubbles: true, composed: true }))
									subscriber.next(); subscriber.complete(); return
								}
								svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
								const viewBox = (svg.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/).map(Number)
								if ((!svg.hasAttribute('width') || !svg.hasAttribute('height')) && viewBox.length === 4 && viewBox.every(Number.isFinite) && viewBox[2]! > 0 && viewBox[3]! > 0) {
									svg.setAttribute('width', String(viewBox[2]))
									svg.setAttribute('height', String(viewBox[3]))
								}
								const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' }))
								const image = new Image()
								image.onload = () => {
									if (subscriber.closed) return
									try {
										if (!image.naturalWidth || !image.naturalHeight) throw new Error('The SVG has no drawable size.')
										this.svgShape = image
										this.initNeurons()
										this.hasDrawn = false
										this.dispatchEvent(new CustomEvent('shape-load', { bubbles: true, composed: true }))
									} catch {
										this.svgShape = null
										this.initNeurons()
										this.dispatchEvent(new CustomEvent('shape-error', { detail: { message: componentText('thinking-bubble.undrawableSvg') }, bubbles: true, composed: true }))
									}
									subscriber.next(); subscriber.complete()
								}
								image.onerror = () => {
									this.svgShape = null
									this.initNeurons()
									this.hasDrawn = false
									this.dispatchEvent(new CustomEvent('shape-error', { detail: { message: componentText('thinking-bubble.unloadedSvg') }, bubbles: true, composed: true }))
									subscriber.next(); subscriber.complete()
								}
								image.src = url
								return () => { image.onload = null; image.onerror = null; URL.revokeObjectURL(url) }
							})),
						),
						// A new box re-sizes the drawing; resizing clears the canvas, so a
						// still frame is drawn again.
						fromResizeObserver(this).pipe(
							map(() => `${this.offsetWidth}:${this.offsetHeight}:${window.devicePixelRatio}`),
							startWith(`${this.offsetWidth}:${this.offsetHeight}:${window.devicePixelRatio}`),
							distinctUntilChanged(),
							skip(1),
							tap(() => {
								this.sizeCanvas()
								this.hasDrawn = false
							}),
						),
						intersection$(this).pipe(
							tap(visible => {
								this.isVisible = visible
							}),
						),
						fromEvent(document, 'visibilitychange').pipe(
							tap(() => {
								this.tabVisible = document.visibilityState === 'visible'
							}),
						),
						fromEvent(window.matchMedia('(prefers-reduced-motion: reduce)'), 'change'),
						this.animationChanges,
					).pipe(startWith(undefined))
				}),
				tap(() => this.syncRAF()),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	// Sized by the layout box (`offsetWidth`), never a transformed one: a bubble
	// under a scaled ancestor still occupies its own CSS pixels. Compact, it is
	// drawn at the device pixels it occupies, so strokes and dots are floored to
	// whole pixels rather than averaged away by a downscale.
	private sizeCanvas(): void {
		if (!this.canvas || !this.ctx) return
		const size = this.offsetWidth
		const compact = size > 0 && size < COMPACT_BELOW
		this.compactUnit = compact ? W / size : 0
		this.canvas.width = Math.max(1, Math.round((size || W) * (window.devicePixelRatio || 1)))
		this.canvas.height = Math.max(1, Math.round((this.offsetHeight || H) * (window.devicePixelRatio || 1)))
		const scale = this.canvas.width / W
		this.ctx.setTransform(scale, 0, 0, this.canvas.height / H, 0, 0)
		this.cacheSourceFrame()
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback()
		this.stopRAF()
		this.canvas?.remove()
		this.canvas = null
		this.ctx = null
		this.isVisible = false
		this.lastDrawTime = 0
		this.hasDrawn = false
		this.connections.clear()
	}

	protected override updated(changed: PropertyValues<this>): void {
		super.updated(changed)
		if (changed.has('color')) { this.resolveColor(); this.cacheSourceFrame(); this.hasDrawn = false }
		if (changed.has('startSvg') && this.ctx) {
			this.canvas!.className = this.startShape || this.startSvg.trim() ? 'block w-full h-full' : 'block w-full h-full motion-reduce:hidden'
			this.connections.clear()
			// initNeurons() runs once the svgChanges pipeline resolves the new
			// shape (or its absence) — reinitializing here too would scatter the
			// neurons against the still-loading (or stale) shape for a frame.
			this.svgChanges.next()
		}
		if ((changed.has('startWord') || changed.has('wordMotion') || changed.has('wordFont') || changed.has('startShape')) && this.ctx) {
			this.canvas!.className = this.startShape || this.startSvg.trim() ? 'block w-full h-full' : 'block w-full h-full motion-reduce:hidden'
			this.initNeurons()
			this.connections.clear()
			this.hasDrawn = false
		}
		if (changed.has('color') || changed.has('startSvg') || changed.has('animated') || changed.has('startWord') || changed.has('wordMotion') || changed.has('wordFont') || changed.has('startShape')) this.animationChanges.next()
	}

	private resolveColor(): void {
		this.inkColor = null
		this.inkAlpha = 1
		if (!this.color.trim() || !CSS.supports('color', this.color)) return
		const probe = document.createElement('span')
		probe.style.color = this.color
		this.renderRoot.append(probe)
		const resolved = getComputedStyle(probe).color
		probe.remove()
		const canvas = document.createElement('canvas')
		canvas.width = canvas.height = 1
		const context = canvas.getContext('2d')!
		context.fillStyle = resolved
		context.fillRect(0, 0, 1, 1)
		const pixel = context.getImageData(0, 0, 1, 1).data
		this.inkColor = { r: pixel[0]!, g: pixel[1]!, b: pixel[2]! }
		this.inkAlpha = pixel[3]! / 255
	}

	/** Cache the original at device resolution; particle sampling stays bounded and independent. */
	private cacheSourceFrame(): void {
		this.sourceFrame = null
		const source = this.startSvg.trim() ? this.svgShape : this.startShape
		if (!source || !source.width || !source.height || !this.canvas) return
		const frame = document.createElement('canvas')
		frame.width = this.canvas.width
		frame.height = this.canvas.height
		const context = frame.getContext('2d')
		if (!context) return
		context.scale(frame.width / W, frame.height / H)
		const fit = Math.min(208 / source.width, 208 / source.height)
		context.drawImage(source, (W - source.width * fit) / 2, (H - source.height * fit) / 2, source.width * fit, source.height * fit)
		if (this.inkColor) {
			context.globalCompositeOperation = 'source-in'
			context.fillStyle = `rgba(${this.inkColor.r},${this.inkColor.g},${this.inkColor.b},${this.inkAlpha})`
			context.fillRect(0, 0, W, H)
		}
		this.sourceFrame = frame
	}

	private initNeurons(): void {
		this.cacheSourceFrame()
		const source = this.startSvg.trim() ? this.svgShape : this.startShape
		this.sourcePalette = []
		const sourceColors = new Map<number, { color: RGB; coverage: number }>()
		this.neurons = []
		this.nextNeuronId = 0
		this.wordElapsed = 0
		this.wordPoints = []
		this.shapePointRadius = 0.7
		this.shapeMask = null
		this.lastDrawTime = 0
		if (source || (!this.startSvg.trim() && this.startWord.trim())) {
			const mask = document.createElement('canvas')
			const density = source || this.wordMotion ? 2 : 1
			mask.width = W * density
			mask.height = H * density
			const ink = mask.getContext('2d')
			if (ink) {
				ink.scale(density, density)
				if (source) {
					if (source.width > 0 && source.height > 0) {
						const fit = Math.min(208 / source.width, 208 / source.height)
						ink.drawImage(source, (W - source.width * fit) / 2, (H - source.height * fit) / 2, source.width * fit, source.height * fit)
					}
					this.shapeMask = mask
				} else {
					ink.font = `700 64px ${this.wordFont || getComputedStyle(this).fontFamily}`
					const width = ink.measureText(this.startWord.trim()).width
					ink.font = `700 ${Math.min(64, (64 * 208) / Math.max(1, width))}px ${this.wordFont || getComputedStyle(this).fontFamily}`
					ink.textAlign = 'center'
					ink.textBaseline = 'middle'
					ink.fillText(this.startWord.trim(), CX, CY)
				}
				const pixels = ink.getImageData(0, 0, mask.width, mask.height).data
				for (let y = 0; y < mask.height; y += 2) {
					for (let x = 0; x < mask.width; x += 2) {
						// Coverage keeps the font's antialiased contour instead of thresholding
						// every edge into the same opaque, visibly stair-stepped pixel grid.
						const coverage = density === 1 ? Number(pixels[(y * mask.width + x) * 4 + 3]! > 100)
							: (pixels[(y * mask.width + x) * 4 + 3]! + pixels[(y * mask.width + x + 1) * 4 + 3]!
								+ pixels[((y + 1) * mask.width + x) * 4 + 3]! + pixels[((y + 1) * mask.width + x + 1) * 4 + 3]!) / 1020
						if (coverage > 0) {
							const offset = (y * mask.width + x) * 4
							let r = 0, g = 0, b = 0, weight = 0
							if (source) {
								for (const sample of [offset, offset + 4, offset + mask.width * 4, offset + mask.width * 4 + 4]) {
									const a = pixels[sample + 3]!
									r += pixels[sample]! * a
									g += pixels[sample + 1]! * a
									b += pixels[sample + 2]! * a
									weight += a
								}
							}
							if (source) {
								const color = { r: Math.round(r / weight), g: Math.round(g / weight), b: Math.round(b / weight) }
								// Quantized buckets retain dominant ink colors instead of spending
								// palette entries on antialiasing variations of the same material.
								const key = (color.r >> 4) * 256 + (color.g >> 4) * 16 + (color.b >> 4)
								const existing = sourceColors.get(key)
								if (existing) existing.coverage += coverage
								else sourceColors.set(key, { color, coverage })
							}
							this.wordPoints.push({ x: x / density, y: y / density, phase: Math.random() * Math.PI * 2, target: 0, coverage,
								color: source ? `rgb(${Math.round(r / weight)},${Math.round(g / weight)},${Math.round(b / weight)})` : undefined })
						}
					}
				}
			}
		}
		this.sourcePalette = Array.from(sourceColors.values()).sort((a, b) => b.coverage - a.coverage).slice(0, 8).map(entry => entry.color)
		if (source && this.wordPoints.length > 12000) {
			const stride = Math.ceil(this.wordPoints.length / 12000)
			this.wordPoints = this.wordPoints.filter((_, index) => index % stride === 0)
			this.shapePointRadius *= Math.sqrt(stride)
		}
		const now = performance.now()

		for (let i = 0; i < this.seeds; i += 1) {
			const angle = Math.random() * Math.PI * 2
			const dist = Math.random() * (BASE_R - 6)
			const x = CX + dist * Math.cos(angle)
			const y = CY + dist * Math.sin(angle)
			this.createNeuron(x, y, now)
		}

		// Neighbouring ink samples share a destination rather than scattering independently.
		for (const point of this.wordPoints) {
			let distance = Infinity
			for (let i = 0; i < this.neurons.length; i += 1) {
				const next = Math.hypot(point.x - this.neurons[i]!.baseX, point.y - this.neurons[i]!.baseY)
				if (next < distance) {
					distance = next
					point.target = i
				}
			}
		}

		for (let i = 0; i < this.neurons.length; i += 1) {
			this.sinPhase[i] = Math.sin(this.neurons[i]!.phase)
			this.prevSinPhase[i] = this.sinPhase[i]!
		}
	}

	private createNeuron(x: number, y: number, now: number): Neuron {
		const nf = 0.0008 + Math.random() * 0.0017
		const targetR = 2.0 + Math.random() * 2.4
		const neuron: Neuron = {
			x,
			y,
			baseX: x,
			baseY: y,
			phaseX: Math.random() * Math.PI * 2,
			phaseY: Math.random() * Math.PI * 2,
			freqX: 0.0003 + Math.random() * 0.0004,
			freqY: 0.00025 + Math.random() * 0.00035,
			ampX: 12 + Math.random() * 24,
			ampY: 12 + Math.random() * 24,
			radius: 0,
			opacity: 0,
			glowRadius: 0,
			driftVx: (Math.random() - 0.5) * 0.02,
			driftVy: (Math.random() - 0.5) * 0.02,
			phase: Math.random() * Math.PI * 2,
			naturalFreq: nf,
			freqTarget: nf,
			id: this.nextNeuronId++,
			birthTime: now,
			maturity: 0,
			targetRadius: targetR,
			targetOpacity: 0.4 + Math.random() * 0.5,
			targetGlowRadius: targetR * 1.6 + Math.random() * 1.5,
			trace: 0,
		}
		this.neurons.push(neuron)
		return neuron
	}

	private updateNeurogenesis(now: number): void {
		for (const n of this.neurons) {
			const age = now - n.birthTime
			n.maturity = 1 - Math.exp(-age / TAU_MATURE)
			n.radius = n.targetRadius * n.maturity
			n.opacity = n.targetOpacity * n.maturity
			n.glowRadius = n.targetGlowRadius * n.maturity
		}
	}

	private spawnNeuron(now: number): void {
		if (this.neurons.length >= MAX_N) return
		const angle = Math.random() * Math.PI * 2
		const dist = Math.random() * (BASE_R - 10)
		const x = CX + dist * Math.cos(angle)
		const y = CY + dist * Math.sin(angle)
		this.createNeuron(x, y, now)
	}

	private updateSTDP(dt: number): void {
		const nCount = this.neurons.length

		for (let i = 0; i < nCount; i += 1) {
			const n = this.neurons[i]!
			n.trace *= Math.exp(-dt / STDP_TAU)
			if (this.prevSinPhase[i]! < 0 && this.sinPhase[i]! >= 0) {
				n.trace += 1.0
			}
		}

		for (const conn of this.connections.values()) {
			if (conn.i >= nCount || conn.j >= nCount) continue
			const ni = this.neurons[conn.i]!
			const nj = this.neurons[conn.j]!

			if (this.prevSinPhase[conn.i]! < 0 && this.sinPhase[conn.i]! >= 0) {
				conn.weight = Math.max(0, conn.weight - STDP_A_MINUS * nj.trace)
			}
			if (this.prevSinPhase[conn.j]! < 0 && this.sinPhase[conn.j]! >= 0) {
				conn.weight = Math.min(1, conn.weight + STDP_A_PLUS * ni.trace)
			}
		}
	}

	/** Port of nebula.ts:422-433 palette derivation. Pure, called once per frame. */
	private buildPalette(): Palette {
		const temp = this.temperature
		const warmShift = Math.max(0, temp)
		const coolShift = Math.max(0, -temp)
		return {
			red: { r: 255, g: Math.round(20 + coolShift * 60), b: Math.round(80 + coolShift * 80) },
			blue: { r: Math.round(20 + warmShift * 80), g: 100, b: Math.round(255 - warmShift * 55) },
			pink: { r: 255, g: Math.round(100 + coolShift * 100), b: 200 },
			cyan: { r: Math.round(warmShift * 100), g: Math.round(255 - warmShift * 55), b: 200 },
			magenta: { r: 255, g: Math.round(100 + coolShift * 55), b: Math.round(255 - coolShift * 55) },
		}
	}

	/**
	 * Sample the spatial nebula gradient at a given distance ratio + iridescence phase.
	 * Core anchor (distRatio 0) = magenta/pink, shimmering on nebula's 28 s period.
	 * Edge anchor (distRatio 1) = blue/cyan, counter-phase to the core.
	 * Returns numeric [r, g, b] so callers can apply warmth arithmetic before formatting.
	 */
	private samplePalette(pal: Palette, distRatio: number, iri: number): [number, number, number] {
		if (this.inkColor) return [this.inkColor.r, this.inkColor.g, this.inkColor.b]
		if (this.sourcePalette.length) {
			const position = Math.min(1, Math.max(0, distRatio)) * (this.sourcePalette.length - 1)
			const from = this.sourcePalette[Math.floor(position)]!
			const to = this.sourcePalette[Math.min(this.sourcePalette.length - 1, Math.ceil(position))]!
			const mix = position % 1
			return [from.r + (to.r - from.r) * mix, from.g + (to.g - from.g) * mix, from.b + (to.b - from.b) * mix]
		}
		const coreT = 0.5 + 0.5 * Math.sin(Math.PI * 2 * iri)
		const edgeT = 0.5 + 0.5 * Math.sin(Math.PI * 2 * iri + Math.PI)
		const d = Math.min(1, Math.max(0, distRatio))
		const cr = pal.magenta.r + (pal.pink.r - pal.magenta.r) * coreT
		const cg = pal.magenta.g + (pal.pink.g - pal.magenta.g) * coreT
		const cb = pal.magenta.b + (pal.pink.b - pal.magenta.b) * coreT
		const er = pal.blue.r + (pal.cyan.r - pal.blue.r) * edgeT
		const eg = pal.blue.g + (pal.cyan.g - pal.blue.g) * edgeT
		const eb = pal.blue.b + (pal.cyan.b - pal.blue.b) * edgeT
		return [cr + (er - cr) * d, cg + (eg - cg) * d, cb + (eb - cb) * d]
	}

	/** Format a sampled palette position as a CSS rgba() string. */
	private paletteColor(pal: Palette, distRatio: number, iri: number, alpha: number): string {
		const [r, g, b] = this.samplePalette(pal, distRatio, iri)
		return `rgba(${r | 0},${g | 0},${b | 0},${(alpha * this.inkAlpha).toFixed(3)})`
	}

	private syncRAF(): void {
		if (!this.isVisible || !this.tabVisible || !this.isConnected) {
			this.stopRAF()
			return
		}
		if (reducedMotion()) {
			this.stopRAF()
			this.ctx?.clearRect(0, 0, W, H)
			if (this.shapeMask && this.ctx) {
				this.ctx.drawImage(this.sourceFrame ?? this.shapeMask, 0, 0, W, H)
				if (this.inkColor && !this.sourceFrame) {
					this.ctx.globalCompositeOperation = 'source-in'
					this.ctx.fillStyle = `rgba(${this.inkColor.r},${this.inkColor.g},${this.inkColor.b},${this.inkAlpha})`
					this.ctx.fillRect(0, 0, W, H)
					this.ctx.globalCompositeOperation = 'source-over'
				}
			}
			return
		}
		if (this.animated) this.startRAF()
		else {
			this.stopRAF()
			if (!this.hasDrawn) {
				const now = performance.now()
				// The frozen image needs formed neurons rather than their transparent birth frame.
				for (const neuron of this.neurons) neuron.birthTime = now - TAU_MATURE * 5
				this.draw(now)
			}
		}
	}

	private startRAF(): void {
		if (this.rafId) return
		this.rafId = requestAnimationFrame(this.draw)
	}

	private stopRAF(): void {
		if (this.rafId) {
			cancelAnimationFrame(this.rafId)
			this.rafId = 0
		}
	}

	private draw = (timestamp: number): void => {
		const ctx = this.ctx
		if (!ctx) return
		this.hasDrawn = true

		const dt = this.lastDrawTime > 0 ? Math.min(timestamp - this.lastDrawTime, 50) : 16
		this.lastDrawTime = timestamp
		const now = performance.now()
		const dtNorm = dt / 16
		if (this.animated) this.wordElapsed += dt
		const organic = Boolean(this.wordMotion) && this.wordPoints.length > 0
		const quiet = organic && this.wordMotion === 'breath'
		const hold = quiet ? 1700 : organic ? 2300 : 1500
		const duration = quiet ? 4800 : organic ? 3000 : 2200
		let motionElapsed = this.wordElapsed
		if (this.loop && this.wordPoints.length) {
			const phase = this.wordElapsed % (hold * 2 + duration * 2 + 3000)
			motionElapsed = phase <= hold + duration + 3000
				? Math.min(phase, hold + duration)
				: Math.max(hold, hold + duration - (phase - hold - duration - 3000))
		}
		const release = this.wordPoints.length ? Math.min(1, Math.max(0, (motionElapsed - hold) / duration)) : 1
		const unfold = release * release * (3 - 2 * release)

		const prevNCount = this.neurons.length
		for (let i = 0; i < prevNCount; i += 1) {
			this.prevSinPhase[i] = this.sinPhase[i]!
		}

		this.updateNeurogenesis(now)

		const nCount = this.neurons.length

		for (let i = 0; i < nCount; i += 1) {
			const pi = this.neurons[i]!

			pi.phase += pi.naturalFreq * dt

			pi.naturalFreq += (pi.freqTarget - pi.naturalFreq) * 0.0005 * dtNorm
			if (Math.abs(pi.naturalFreq - pi.freqTarget) < 0.0001) {
				pi.freqTarget = 0.0008 + Math.random() * 0.0017
			}

			for (let j = 0; j < nCount; j += 1) {
				if (i === j) continue
				const pj = this.neurons[j]!
				const dx = pi.x - pj.x
				const dy = pi.y - pj.y
				const dist = Math.sqrt(dx * dx + dy * dy)
				if (dist < CONNECT_DIST) {
					const ki = Math.min(i, j)
					const kj = Math.max(i, j)
					const key = ki * MAX_N + kj
					const conn = this.connections.get(key)
					const k = conn ? conn.coupling : COUPLING
					pi.phase += (1 - dist / CONNECT_DIST) * k * pi.maturity * pj.maturity * dt * Math.sin(pj.phase - pi.phase)
				}
			}
		}

		for (let i = 0; i < nCount; i += 1) {
			this.sinPhase[i] = Math.sin(this.neurons[i]!.phase)
		}

		this.updateSTDP(dt)

		const harmonicWt = HARMONICS.map(
			h => h.speed * timestamp + 0.3 * Math.sin(timestamp * h.speed * 0.37 + h.phase * 2.1),
		)

		for (const p of this.neurons) {
			p.driftVx += (Math.random() - 0.5) * 0.008
			p.driftVy += (Math.random() - 0.5) * 0.008
			p.driftVx *= 0.98
			p.driftVy *= 0.98
			p.baseX += p.driftVx * dtNorm
			p.baseY += p.driftVy * dtNorm

			const dbx = p.baseX - CX
			const dby = p.baseY - CY
			const bd = Math.sqrt(dbx * dbx + dby * dby)
			if (bd > BASE_R - 6) {
				const s = (BASE_R - 6) / bd
				p.baseX = CX + dbx * s
				p.baseY = CY + dby * s
				p.driftVx *= -0.5
				p.driftVy *= -0.5
			}

			p.x = p.baseX + p.ampX * Math.sin(p.freqX * timestamp + p.phaseX)
			p.y = p.baseY + p.ampY * Math.sin(p.freqY * timestamp + p.phaseY)

			const dx = p.x - CX
			const dy = p.y - CY
			const dist = Math.sqrt(dx * dx + dy * dy)
			if (dist > 1) {
				const theta = Math.atan2(dy, dx)
				let blobR = BASE_R
				for (let hIdx = 0; hIdx < HARMONICS.length; hIdx += 1) {
					const h = HARMONICS[hIdx]!
					blobR += h.amp * Math.sin(h.n * theta + harmonicWt[hIdx]! + h.phase)
				}
				const maxR = blobR - 6
				if (dist > maxR) {
					const s = maxR / dist
					p.x = CX + dx * s
					p.y = CY + dy * s
				}
			}
		}

		this.activeKeys.clear()
		for (let i = 0; i < nCount; i += 1) {
			for (let j = i + 1; j < nCount; j += 1) {
				const dx = this.neurons[i]!.x - this.neurons[j]!.x
				const dy = this.neurons[i]!.y - this.neurons[j]!.y
				const dist = Math.sqrt(dx * dx + dy * dy)
				if (dist < CONNECT_DIST) {
					const key = i * MAX_N + j
					this.activeKeys.add(key)
					let conn = this.connections.get(key)
					if (!conn) {
						conn = {
							i,
							j,
							alpha: 0,
							cpx: (Math.random() - 0.5) * 6,
							cpy: (Math.random() - 0.5) * 6,
							cpvx: (Math.random() - 0.5) * 0.01,
							cpvy: (Math.random() - 0.5) * 0.01,
							weight: 0.3,
							coupling: COUPLING,
						}
						this.connections.set(key, conn)
					}
					const target = 1 - dist / CONNECT_DIST
					// A compact still frame is drawn once, so its connections start formed.
					conn.alpha = this.compactUnit && !this.animated ? target : conn.alpha + (target - conn.alpha) * 0.08 * dtNorm

					const phaseDiff = this.neurons[i]!.phase - this.neurons[j]!.phase
					conn.coupling += HEBBIAN_EPSILON * dt * (HEBBIAN_ALPHA * Math.cos(phaseDiff) - conn.coupling)
					conn.coupling = Math.max(0, Math.min(1, conn.coupling))
				}
			}
		}

		for (const [key, conn] of this.connections) {
			conn.cpvx += (Math.random() - 0.5) * 0.02
			conn.cpvy += (Math.random() - 0.5) * 0.02
			conn.cpvx *= 0.95
			conn.cpvy *= 0.95
			conn.cpx += conn.cpvx * dtNorm
			conn.cpy += conn.cpvy * dtNorm
			const cm = Math.sqrt(conn.cpx * conn.cpx + conn.cpy * conn.cpy)
			if (cm > 15) {
				conn.cpx *= 15 / cm
				conn.cpy *= 15 / cm
			}

			if (!this.activeKeys.has(key)) {
				conn.alpha -= FADE_RATE * dt
				if (conn.alpha <= 0) this.connections.delete(key)
			}
		}

		ctx.clearRect(0, 0, W, H)

		// Nebula palette + iridescence phase — computed once, sampled per draw site.
		const pal = this.buildPalette()
		const iri = (timestamp / 28000) % 1
		ctx.globalAlpha = quiet ? Math.max(0, (unfold - 0.72) / 0.28) : organic ? Math.max(0, (unfold - 0.35) / 0.65) : unfold

		for (const conn of this.connections.values()) {
			if (conn.alpha < 0.01) continue
			if (conn.i >= nCount || conn.j >= nCount) continue
			const pi = this.neurons[conn.i]!
			const pj = this.neurons[conn.j]!

			const phaseAlign = 0.5 + 0.5 * Math.cos(pi.phase - pj.phase)
			const meanBright = 0.5 * (Math.sin(pi.phase) + Math.sin(pj.phase)) * 0.5 + 0.5

			const alpha = Math.min(1, conn.alpha * (0.15 + phaseAlign * 0.5) * (0.5 + meanBright))
			const mx = (pi.x + pj.x) / 2
			const my = (pi.y + pj.y) / 2
			// Connection colour = mean of the two endpoints' spatial positions.
			const ri = Math.min(1, Math.hypot(pi.x - CX, pi.y - CY) / BASE_R)
			const rj = Math.min(1, Math.hypot(pj.x - CX, pj.y - CY) / BASE_R)

			ctx.beginPath()
			ctx.moveTo(pi.x, pi.y)
			ctx.quadraticCurveTo(mx + conn.cpx, my + conn.cpy, pj.x, pj.y)
			ctx.strokeStyle = this.paletteColor(pal, (ri + rj) / 2, iri, alpha)
			ctx.lineWidth = Math.max(conn.alpha * (0.6 + phaseAlign * 1.0), this.compactUnit)
			ctx.stroke()
		}

		ctx.globalAlpha = quiet ? Math.max(0, (unfold - 0.62) / 0.38) : ctx.globalAlpha
		ctx.globalCompositeOperation = this.inkColor || this.sourcePalette.length ? 'source-over' : 'lighter'

		for (let i = 0; i < nCount; i += 1) {
			const p = this.neurons[i]!
			if (p.opacity < 0.01) continue

			const brightness = 0.5 + 0.5 * this.sinPhase[i]!
			const glow = p.glowRadius * (0.8 + brightness * 0.4)

			// Sample palette at neuron's radial position, then push bright neurons toward white.
			const rN = Math.min(1, Math.hypot(p.x - CX, p.y - CY) / BASE_R)
			const [nr, ng, nb] = this.samplePalette(pal, rN, iri)
			const warmth = this.inkColor || this.sourcePalette.length ? 0 : brightness * brightness * brightness * 0.25
			const pr = Math.min(255, nr + (255 - nr) * warmth) | 0
			const pg = Math.min(255, ng + (255 - ng) * warmth) | 0
			const pb = Math.min(255, nb + (255 - nb) * warmth) | 0

			if (glow > 0.1) {
				const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, glow)
				grad.addColorStop(0, `rgba(${pr},${pg},${pb},${(p.opacity * 0.4 * (0.5 + brightness * 0.5) * this.inkAlpha).toFixed(2)})`)
				grad.addColorStop(1, this.paletteColor(pal, rN, iri, 0))
				ctx.fillStyle = grad
				ctx.beginPath()
				ctx.arc(p.x, p.y, glow, 0, Math.PI * 2)
				ctx.fill()
			}

			if (p.radius > 0.05) {
				const coreAlpha = p.opacity * (0.5 + brightness * 0.5)
				ctx.fillStyle = `rgba(${pr},${pg},${pb},${((this.compactUnit ? Math.max(coreAlpha, 0.9) : coreAlpha) * this.inkAlpha).toFixed(2)})`
				ctx.beginPath()
				ctx.arc(p.x, p.y, Math.max(p.radius, this.compactUnit * 1.25), 0, Math.PI * 2)
				ctx.fill()
			}
		}

		ctx.globalCompositeOperation = 'source-over'
		ctx.globalAlpha = 1
		if (release < 1) {
			const enter = this.animated ? Math.min(1, this.wordElapsed / (quiet ? 700 : 1100)) : 1
			const arrived = enter * enter * (3 - 2 * enter)
			const feather = this.sourceFrame ? Math.min(1, release / 0.16) : 1
			const atoms = feather * feather * (3 - 2 * feather)
			if (this.sourceFrame && atoms < 1) {
				ctx.globalAlpha = arrived * (1 - atoms)
				ctx.drawImage(this.sourceFrame, 0, 0, W, H)
			}

			for (let i = 0; atoms > 0 && i < this.wordPoints.length; i += 1) {
				const point = this.wordPoints[i]!
				const neuron = this.neurons[organic ? point.target : i % nCount]
				if (!neuron) continue
				let x: number
				let y: number
				let alpha = 1 - unfold
				if (organic) {
					const local = this.wordMotion === 'current'
						? Math.min(1, Math.max(0, (motionElapsed - 2300 - point.x / W * 400) / 2600))
						: quiet ? Math.max(0, (release - point.phase / (Math.PI * 2) * 0.12) / (1 - point.phase / (Math.PI * 2) * 0.12)) : release
					const flow = local * local * local * (local * (local * 6 - 15) + 10)
					const arch = Math.sin(Math.PI * flow)
					const dx = neuron.x - point.x
					const dy = neuron.y - point.y
					x = point.x + dx * flow
					y = point.y + dy * flow
					if (this.wordMotion === 'orbit') {
						x -= dy * arch * 0.65
						y += dx * arch * 0.65
						x += (1 - arrived) * (point.y - CY) * 0.7
						y -= (1 - arrived) * (point.x - CX) * 0.25
					} else if (this.wordMotion === 'current') {
						y += Math.sin(point.x / W * Math.PI * 2 - flow * Math.PI) * arch * 18
						x -= (1 - arrived) * 16
						y += Math.sin(point.x / W * Math.PI * 2) * (1 - arrived) * 12
					} else {
						// Staggered grains separate from the ink, then travel along shallow arcs.
						// Their paths meet the actual nodes; opacity yields only at arrival.
						const bend = Math.sin(point.phase) * arch * 7
						const distance = Math.max(1, Math.hypot(dx, dy))
						x -= dy / distance * bend
						y += dx / distance * bend
					}
					if (quiet) {
						const arrival = Math.min(1, Math.max(0, (flow - 0.9) / 0.1))
						alpha = arrived * (1 - arrival * arrival * (3 - 2 * arrival))
					} else alpha = arrived * (1 - Math.max(0, (flow - 0.55) / 0.45))
				} else {
					const swirl = Math.sin(release * Math.PI) * 22
					x = point.x + (neuron.x - point.x) * unfold + Math.sin(point.phase + release * 5) * swirl
					y = point.y + (neuron.y - point.y) * unfold + Math.cos(point.phase + release * 5) * swirl
				}
				alpha *= atoms
				ctx.fillStyle = (this.inkColor ? undefined : point.color) ?? this.paletteColor(pal, Math.abs(point.x - CX) / CX, iri, alpha * point.coverage)
				ctx.globalAlpha = point.color && !this.inkColor ? alpha * point.coverage : 1
				ctx.beginPath()
				ctx.arc(x, y, (this.shapeMask ? this.shapePointRadius : organic ? 0.7 : 1.15) * (quiet ? 1 - Math.sin(Math.PI * release) * 0.28 : 1), 0, Math.PI * 2)
				ctx.fill()
			}
		}
		ctx.globalAlpha = 1
		// Autonomous growth — gentle drift toward a resting population.
		const restingPop = 10
		if (this.animated && nCount < MAX_N) {
			const hunger = Math.max(0, 1 - nCount / restingPop)
			const chance = 0.002 + hunger * 0.008
			if (Math.random() < chance) {
				this.spawnNeuron(now)
			}
		}

		if (this.animated) this.rafId = requestAnimationFrame(this.draw)
	}

	override render() {
		return html`<div class="w-full h-full" ${ref(this.wrapperRef)}>
			<span
				class=${
					this.startShape || this.startSvg.trim() ? 'hidden' : this.startWord.trim()
						? 'hidden motion-reduce:flex items-center justify-center w-full h-full text-primary-default font-bold text-4xl break-all'
						: 'hidden motion-reduce:block w-full h-full rounded-full bg-primary-default opacity-40'
				}
				style=${styleMap({ color: this.color || undefined, backgroundColor: this.startWord.trim() ? undefined : this.color || undefined })}
				aria-hidden="true"
				>${this.startWord.trim()}</span
			>
		</div>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-thinking-bubble': SchmancyThinkingBubble
	}
}
